import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/cn";
import { clientLogTimes } from "@/lib/dayHelpers";
import {
  DEFAULT_BODY_WEIGHT_KG,
  EMPTY_FORM,
  estimateKcal,
  getErrorMessage,
  lookupMet,
  parseMetrics,
  parseNumber,
} from "./workoutFormUtils";
import type { WorkoutFormState } from "./workoutFormUtils";

type WorkoutFormProps = {
  readonly date: number;
  readonly onSaved?: () => void;
};

type ExerciseEntry = Doc<"exerciseEntries">;

export function WorkoutForm({ date, onSaved }: WorkoutFormProps) {
  const profile = useQuery(api.profile.getProfile, {});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<WorkoutFormState>(EMPTY_FORM);
  const inferredMet = useMemo(() => lookupMet(form.type), [form.type]);
  const explicitMet = parseNumber(form.met);
  const met = explicitMet !== undefined && explicitMet > 0 ? explicitMet : inferredMet;
  const durationMin = parseNumber(form.duration);
  const weightKg = parseNumber(form.weight) ?? profile?.weightKg ?? DEFAULT_BODY_WEIGHT_KG;
  const estimatedKcal = durationMin !== undefined && durationMin > 0
    ? estimateKcal(met, weightKg, durationMin)
    : null;

  const createLoggedExercise = useMutation(api.entries.createLoggedExercise).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.dashboard.getByDate, { date: args.date, clientDayStart: args.clientDayStart });
      if (current === undefined) return;
      const optimisticMet = args.met ?? lookupMet(args.type);
      const optimisticWeightKg = args.weightKg ?? profile?.weightKg ?? DEFAULT_BODY_WEIGHT_KG;
      const burnedKcal = estimateKcal(optimisticMet, optimisticWeightKg, args.durationMin);
      const now = Date.now();
      const temporaryEntry: ExerciseEntry = {
        _id: `temp_${now}_${Math.random().toString(36).slice(2)}` as Id<"exerciseEntries">,
        _creationTime: now,
        userId: "optimistic" as Id<"users">,
         date: args.clientDayStart ?? args.date,
        type: args.type,
        durationMin: args.durationMin,
        distanceM: args.distanceM,
        reps: args.reps,
        sets: args.sets,
        weightKg: args.weightKg,
        met: optimisticMet,
        estimatedBurnedKcal: burnedKcal,
        finalBurnedKcal: burnedKcal,
        source: "text",
        status: "logged",
        notes: args.notes,
        loggedAt: now,
      };
       localStore.setQuery(api.dashboard.getByDate, { date: args.date, clientDayStart: args.clientDayStart }, {
        ...current,
        burnedKcal: current.burnedKcal + burnedKcal,
        netKcal: current.netKcal - burnedKcal,
        remainingKcal: current.remainingKcal + burnedKcal,
        exerciseEntries: [...current.exerciseEntries, temporaryEntry],
      });
    },
  );

  const update = (field: keyof WorkoutFormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const type = form.type.trim();
    if (type.length === 0) {
      setError("Add an exercise type first.");
      return;
    }
    if (durationMin === undefined || durationMin <= 0) {
      setError("Duration must be greater than zero.");
      return;
    }
    if (explicitMet !== undefined && explicitMet <= 0) {
      setError("MET must be greater than zero.");
      return;
    }
    const parsedMetrics = parseMetrics(form);
    if (!parsedMetrics.ok) {
      setError(parsedMetrics.message);
      return;
    }

    setIsSaving(true);
    console.log("[Frontend][WorkoutForm] Logging workout", { date, type, durationMin, met, estimatedKcal });
    try {
      await createLoggedExercise({
        date,
        ...clientLogTimes(date),
        type,
        durationMin,
        met,
        ...(parsedMetrics.metrics.distanceM !== undefined ? { distanceM: parsedMetrics.metrics.distanceM } : {}),
        ...(parsedMetrics.metrics.reps !== undefined ? { reps: parsedMetrics.metrics.reps } : {}),
        ...(parsedMetrics.metrics.sets !== undefined ? { sets: parsedMetrics.metrics.sets } : {}),
        ...(parsedMetrics.metrics.weightKg !== undefined ? { weightKg: parsedMetrics.metrics.weightKg } : {}),
        ...(form.notes.trim().length > 0 ? { notes: form.notes.trim() } : {}),
      });
      setForm(EMPTY_FORM);
      setError(null);
      onSaved?.();
    } catch (err: unknown) {
      console.error("[Frontend][WorkoutForm] Workout logging failed", { date, type, error: getErrorMessage(err) });
      setError("Could not log that workout. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div data-testid="workout-form" className="text-left">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Input data-testid="workout-type-input" label="Type" value={form.type} onChange={(event) => update("type", event.target.value)} placeholder="e.g. Running" aria-required="true" />
          </div>
          <Input data-testid="workout-duration-input" label="Duration (min)" type="number" min="1" step="1" inputMode="numeric" value={form.duration} onChange={(event) => update("duration", event.target.value)} placeholder="30" aria-required="true" />
          <Input data-testid="workout-met-input" label="MET" type="number" min="0" step="0.1" inputMode="decimal" value={form.met} onChange={(event) => update("met", event.target.value)} placeholder={`${inferredMet}`} />
          <Input data-testid="workout-distance-input" label="Distance (m)" type="number" min="0" step="1" inputMode="numeric" value={form.distance} onChange={(event) => update("distance", event.target.value)} placeholder="Optional" />
          <Input data-testid="workout-weight-input" label="Weight (kg)" type="number" min="0" step="0.1" inputMode="decimal" value={form.weight} onChange={(event) => update("weight", event.target.value)} placeholder={`${profile?.weightKg ?? DEFAULT_BODY_WEIGHT_KG}`} />
          <Input data-testid="workout-reps-input" label="Reps" type="number" min="0" step="1" inputMode="numeric" value={form.reps} onChange={(event) => update("reps", event.target.value)} placeholder="Optional" />
          <Input data-testid="workout-sets-input" label="Sets" type="number" min="0" step="1" inputMode="numeric" value={form.sets} onChange={(event) => update("sets", event.target.value)} placeholder="Optional" />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="workout-notes" className="text-sm font-medium text-text">Notes</label>
          <textarea
            id="workout-notes"
            data-testid="workout-notes-input"
            value={form.notes}
            onChange={(event) => update("notes", event.target.value)}
            placeholder="Pace, intensity, how it felt..."
            rows={3}
            className={cn(
              "w-full resize-none rounded-xl border border-border bg-surface px-4 py-3 text-base text-text placeholder:text-text-muted",
              "focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
            )}
          />
        </div>

        <div className="rounded-xl bg-accent-light px-4 py-3" data-testid="workout-kcal-preview">
          <p className="text-xs font-semibold uppercase tracking-wide text-accent">Estimated burn</p>
          <p className="mt-1 text-lg font-semibold text-text">{estimatedKcal === null ? "Enter duration" : `${estimatedKcal} kcal`}</p>
          <p className="text-xs text-text-muted">MET {met} · {weightKg} kg reference weight</p>
        </div>

        {error !== null && <p className="rounded-xl bg-danger/10 px-3 py-2 text-sm font-medium text-danger" role="alert">{error}</p>}
        <Button data-testid="submit-workout-btn" type="submit" loading={isSaving}>Save workout</Button>
      </form>
    </div>
  );
}
